/**
 * Mounting Option Picker
 *
 * Single-select card UI that replaces the dropdown for "Mounting Option".
 * Clicking a card selects it and deselects the previous one.
 *
 * Dependencies:
 * - Must be inside a <variant-picker> element
 */

if (!customElements.get('mounting-option-picker')) {
  class MountingOptionPicker extends HTMLElement {
    constructor() {
      super();

      this.cards = this.querySelectorAll('.mounting-card');
      this.checkboxes = this.querySelectorAll('.mounting-card__check');
      this.hiddenInput = this.querySelector('input[type="radio"].js-option');

      this.checkboxes.forEach((cb) => {
        cb.addEventListener('change', this.handleChange.bind(this));
      });
    }

    handleChange(evt) {
      evt.stopPropagation();

      const checkbox = evt.target;

      if (checkbox.checked) {
        // Uncheck all others — single select
        this.checkboxes.forEach((cb) => {
          if (cb !== checkbox) cb.checked = false;
        });
      } else {
        // Don't allow unchecking the only selected option — re-check it
        checkbox.checked = true;
        return;
      }

      this.updateCardStates();

      // Update hidden input and notify variant-picker
      this.hiddenInput.value = checkbox.dataset.value;
      this.hiddenInput.checked = true;
      this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateCardStates() {
      this.cards.forEach((card) => {
        const cb = card.querySelector('.mounting-card__check');
        card.classList.toggle('mounting-card--selected', cb.checked);
      });
    }
  }

  customElements.define('mounting-option-picker', MountingOptionPicker);
}

/**
 * Default attachment selection
 * ----------------------------
 * Pre-select a default attachment so the variant is complete on first load
 * instead of leaving the picker empty. Runs on every viewport (mobile JS
 * previously; broadened Sep 26 per CRO redesign).
 *
 *  - Default is the "+$0" / "...only" option. The Mini Interior Mount has no
 *    free option, so it falls back to "Standard Suction Cups".
 *  - Never overrides a ?variant= deep link (ads, Shopping feed, cart edits)
 *    or a selection that is already present.
 *  - Fires the same change event a real tap fires, so price, URL, media and
 *    the Add to cart button update through the component's normal path.
 */
(function () {
  // Prefer the free / base ("None") option so the picker matches
  // Shopify's default first variant on load.
  var PRIORITY = [
    /\(\+\s*\$?\s*0\)|\bonly\b|^no\s|^none\b/i
  ];

  function applyDefault(picker) {
    if (picker.dataset.defaultApplied) return;
    picker.dataset.defaultApplied = '1';

    var checks = Array.prototype.slice.call(
      picker.querySelectorAll('.mounting-card__check')
    );
    if (!checks.length) return;

    // Respect anything already selected (theme state, bfcache, etc.)
    if (checks.some(function (c) { return c.checked; })) return;
    var hidden = picker.querySelector('input[type="radio"].js-option');
    if (hidden && hidden.value) return;

    var pick = null;
    for (var i = 0; i < PRIORITY.length && !pick; i++) {
      pick = checks.find(function (c) {
        return !c.disabled && PRIORITY[i].test(c.dataset.value || '');
      });
    }
    if (!pick) pick = checks.find(function (c) { return !c.disabled; }) || checks[0];
    if (!pick || pick.disabled) return;

    pick.checked = true;
    picker.setAttribute('data-cro-default', pick.dataset.value || '');
    pick.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function run() {
    var pickers = document.querySelectorAll('mounting-option-picker');
    if (!pickers.length) return;

    var ready = customElements.whenDefined
      ? customElements.whenDefined('mounting-option-picker')
      : Promise.resolve();

    ready.then(function () {
      Array.prototype.forEach.call(pickers, applyDefault);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
